import type { ColumnFormatter } from '@/components/datatable/dataTableTypes';

/**
 * Safely extracts the raw value from a field that uses the meta('rawvalue') pattern.
 *
 * In this project, many DTO fields are wrapped in an object that provides a `meta` function.
 * Example: `doc.mimeType.meta('rawvalue')`
 *
 * This helper checks if `meta` exists and is callable, then invokes it with "rawvalue".
 * If the field is missing or `meta` is not a function, it will return `undefined` instead of throwing.
 *
 * @template T - Expected type of the raw value (defaults to any).
 * @param field - The field object that may contain a `meta` function.
 * @returns The extracted raw value, or undefined if not available.
 */
export function metaRaw<T = any>(field: any): T | undefined {
  try {
    return typeof field?.meta === 'function' ? field.meta('rawvalue') : undefined;
  } catch {
    return undefined;
  }
}

export const makeCurrencyFormatter =
  (currency: string): ColumnFormatter =>
  (rawValue, { locale }) => {
    const num = Number(rawValue);
    if (!Number.isFinite(num)) return '';

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

export const makeNumberFormatter =
  (minimumFractionDigits: number = 2, maximumFractionDigits: number = 2): ColumnFormatter =>
  (rawValue, { locale }) => {
    const num = Number(rawValue);
    if (!Number.isFinite(num)) return '';

    return new Intl.NumberFormat(locale, {
      style: 'decimal',
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(num);
  };

export const makeIntegerFormatter =
  (): ColumnFormatter =>
  (rawValue, { locale }) => {
    const num = Number(rawValue);
    if (!Number.isFinite(num)) return '';

    return new Intl.NumberFormat(locale, {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

export const makeSquareFeetFormatter =
  (): ColumnFormatter =>
  (rawValue, { locale }) => {
    const num = Number(rawValue);
    if (!Number.isFinite(num)) return '';

    const formatted = new Intl.NumberFormat(locale, {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(num);

    return `${formatted} m²`;
  };

export const makeYearFormatter = (): ColumnFormatter => (rawValue) => {
  if (!rawValue) return '';

  // Parse ISO date string or Date object
  const date = typeof rawValue === 'string' ? new Date(rawValue) : rawValue;
  if (isNaN(date.getTime())) return '';

  return date.getFullYear().toString();
};

export const makeShortDateFormatter = (): ColumnFormatter => (rawValue) => {
  if (!rawValue) return '';

  // Parse ISO date string or Date object
  const date = typeof rawValue === 'string' ? new Date(rawValue) : rawValue;
  if (isNaN(date.getTime())) return '';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);

  return `${day}.${month}.${year}`;
};

// Helper: only treat simple clicks (no modifier keys) as "open detail"
export const isPlainRowClick = (event: { originalEvent?: MouseEvent } | undefined): boolean => {
  const e = event?.originalEvent;
  if (!e) {
    // Fallback: if we don't get a MouseEvent, keep old behaviour
    return true;
  }

  // If any modifier key is pressed, we don't execute the following code
  return !(e.altKey || e.ctrlKey || e.metaKey || e.shiftKey);
};
